import React from 'react';
import { isEmpty, isBlank } from 'x/modules/util';
import {
  beneNameRegex,
  ifscRegEx,
  isAccountNumberValidator,
  fundAccountTypes,
  paymentTermsItems,
} from 'x/modules';
import {
  fundAccountTypeOptions,
  mandatoryFieldsForVendorPreferences,
  vendorPreferencesApiMapping,
  vendorPreferencesSteps,
} from '../constants';

export const validateAccountNumber = (accountNumber) => {
  if (accountNumber) {
    const message = isAccountNumberValidator(accountNumber);
    const accLength = accountNumber.length;
    if (accLength > 35) return 'Account No. cannot be greater than 35 digits';
    if (accLength < 5) return 'Account No. should be of minimum 5 digits';
    return message;
  }
  return '';
};

export const isValidBeneficiaryName = (value) => {
  if (value) {
    if (!beneNameRegex.test(value)) return 'Invalid beneficiary name';
    if (value.length > 120) return 'Beneficiary name cannot be greater than 120 characters';
  }
  return '';
};

export const isValidIfscCode = (ifsc) => (ifsc && !ifscRegEx.test(ifsc) ? 'Invalid IFSC Code' : '');

export const reValidateAccountNumber = (value, accountNumber) => {
  return value && accountNumber && value !== accountNumber ? 'Account no. does not match' : '';
};

export const formatPaymentTerms = (terms) => (Array.isArray(terms) ? Number(terms[1]) : terms);

const getFundAccountPayload = (fundAccount) => {
  const { name: holderName, ifsc, bankName, accountNumber, accountType, vpa } = fundAccount;
  const payload = { [vendorPreferencesApiMapping.accountType]: accountType };
  if (accountType === fundAccountTypes.bankAccount) {
    payload[accountType] = {
      name: holderName,
      ifsc,
      [vendorPreferencesApiMapping.bankName]: bankName,
      [vendorPreferencesApiMapping.accountNumber]: accountNumber,
    };
  } else if (accountType === fundAccountTypes.vpa) {
    payload[accountType] = {
      [vendorPreferencesApiMapping.address]: vpa,
    };
  }
  return payload;
};

export const createVendorPreferencesPayload = (data) => {
  const { contact, gstin, pan, tdsCategoryId, paymentTerms, fundAccount } = data;
  const payload = { contact, gstin, pan };
  payload[vendorPreferencesApiMapping.tdsCategoryId] = tdsCategoryId;

  if (paymentTerms) {
    payload[vendorPreferencesApiMapping.paymentTerms] = formatPaymentTerms(paymentTerms);
  }

  if (!isEmpty(fundAccount)) {
    payload[vendorPreferencesApiMapping.fundAccount] = getFundAccountPayload(fundAccount);
  }
  return payload;
};

const areMandatoryStepsFilled = (fields, data) => {
  if (fields) {
    return fields.every((field) => data?.[field]);
  }
  return false;
};

export const calculateCompletedSteps = (data) => {
  if (isEmpty(data)) return vendorPreferencesSteps.PERSONAL;
  const { personal, business, banking } = mandatoryFieldsForVendorPreferences;

  const fundAccount = data?.fundAccount;

  const arePersonalDetailsFilled = areMandatoryStepsFilled(personal, data);

  const areBusinessDetailsFilled = areMandatoryStepsFilled(business, data);

  const areBankDetailsFilled = areMandatoryStepsFilled(
    banking[fundAccount?.accountType],
    fundAccount,
  );

  if (arePersonalDetailsFilled && areBusinessDetailsFilled && areBankDetailsFilled) {
    return vendorPreferencesSteps.COMPLETE;
  }

  if (!arePersonalDetailsFilled) {
    return vendorPreferencesSteps.PERSONAL;
  }

  if (!areBusinessDetailsFilled) {
    return vendorPreferencesSteps.BUSINESS;
  }

  if (!areBankDetailsFilled) {
    return vendorPreferencesSteps.BANKING;
  }
};

export const isVendorNameValidator = (value, label = '') => {
  const maxLimitMessage = `${label} Name cannot be greater than 255 characters`;
  if (value && value.length > 255) return maxLimitMessage;
  return '';
};

const transformFundAccountResponse = (fundAccount) => {
  const accountType = fundAccount?.[vendorPreferencesApiMapping.accountType];
  const id = fundAccount?.id;
  if (accountType === fundAccountTypes.bankAccount) {
    const bankAccount = fundAccount?.[accountType];
    return {
      accountType,
      id,
      name: bankAccount?.name,
      ifsc: bankAccount?.ifsc,
      bankName: bankAccount?.[vendorPreferencesApiMapping.bankName],
      accountNumber: bankAccount?.[vendorPreferencesApiMapping.accountNumber],
    };
  } else if (accountType === fundAccountTypes.vpa) {
    const vpa = fundAccount?.vpa?.address;
    return { accountType, id, vpa };
  }
  return {};
};

export const transformVendorPreferencesResponse = (data) => {
  const { name, contact, email, gstin, pan } = data;
  const fundAccount = data[vendorPreferencesApiMapping.fundAccount];
  if (!isEmpty(data)) {
    return {
      name,
      contact,
      email,
      gstin,
      pan,
      tdsCategoryId: data[vendorPreferencesApiMapping.tdsCategoryId],
      paymentTerms: data[vendorPreferencesApiMapping.paymentTerms],
      fundAccount: transformFundAccountResponse(fundAccount),
    };
  }
  return {};
};

export const getOnboardingCTAContent = (status, data) => {
  const { banking } = mandatoryFieldsForVendorPreferences;
  const fundAccount = data?.fundAccount;
  const areBankDetailsFilled = areMandatoryStepsFilled(
    banking[fundAccount?.accountType],
    fundAccount,
  );
  switch (status) {
    case 1:
      return 'add business details';
    case 2:
      return areBankDetailsFilled ? 'add business details' : 'add bank details';
    default:
      return 'complete your profile';
  }
};

export const findPaymentTerm = (value) =>
  paymentTermsItems.find((term) => value.toString() === term[1]);

export const removeCountryCode = (number) => number && number.replace(/^\+91/, '');

export const getPanFromGstin = (gstin) => gstin?.slice(2, 12);

export const getOptionalValueLabel = (value) => (
  <>
    {value} <span className="text-white-o-20">(Optional)</span>
  </>
);

export const getFundAccountTypeOptions = (data) => {
  return fundAccountTypeOptions.filter(
    ({ key }) => !(key === fundAccountTypes.vpa && isBlank(data.vpa)),
  );
};
